import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Calendar, Save, Plus, Edit, Trash2, RefreshCw, AlertTriangle, CheckCircle, Users, BarChart3, TrendingDown, Bird } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, ComposedChart, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Pen options (1-6 regular, 7 Brooding, 8 Grower)
const PENS = [
  { value: 1, label: 'Pen 1' },
  { value: 2, label: 'Pen 2' },
  { value: 3, label: 'Pen 3' },
  { value: 4, label: 'Pen 4' },
  { value: 5, label: 'Pen 5' },
  { value: 6, label: 'Pen 6' },
  { value: 7, label: 'Brooding Pen' },
  { value: 8, label: 'Grower Pen' }
];

const MORTALITY_CAUSES = [
  'Disease', 'Heat Stress', 'Cold Stress', 'Accident', 'Predator',
  'Natural', 'Suffocation', 'Starvation', 'Unknown', 'Other'
];

const getPenLabel = (penNum) => {
  const pen = PENS.find(p => p.value === penNum);
  return pen ? pen.label : `Pen ${penNum}`;
};

const ChickenCensus = () => {
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedPen, setSelectedPen] = useState(null);
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // User info
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const token = localStorage.getItem('token');
  const isAdmin = user?.role === 'admin';
  const isManager = user?.role === 'manager';
  const isStaff = user?.role === 'staff';
  const assignedPens = user?.assigned_pens || [];
  
  // Available pens based on role
  const availablePens = (isAdmin || isManager || !assignedPens.length) 
    ? PENS 
    : PENS.filter(p => assignedPens.includes(p.value));
  
  // Form state
  const [formData, setFormData] = useState({
    starting_census: '',
    mortality: '0',
    cause_of_death: '',
    ending_census: '',
    notes: ''
  });
  
  // Physical count dialog
  const [physicalCountOpen, setPhysicalCountOpen] = useState(false);
  const [physicalCountForm, setPhysicalCountForm] = useState({
    physical_count: '',
    notes: ''
  });
  
  // Edit dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  
  // Latest census for selected pen
  const [latestCensus, setLatestCensus] = useState(null);
  
  // Pen mortality from Pen Floor Daily Egg Production
  const [penMortalityData, setPenMortalityData] = useState(null);
  const [loadingMortality, setLoadingMortality] = useState(false);
  
  // Historical data for charts
  const [showCharts, setShowCharts] = useState(false);
  const [historyData, setHistoryData] = useState(null);
  const [historyDays, setHistoryDays] = useState(30);
  const [loadingHistory, setLoadingHistory] = useState(false);
  
  // Chart colors
  const CHART_COLORS = ['#ef4444', '#f97316', '#eab308', '#22c55e', '#3b82f6', '#8b5cf6', '#ec4899', '#06b6d4'];

  const getAuthHeader = () => ({
    headers: { Authorization: `Bearer ${token}` }
  });

  useEffect(() => {
    if (availablePens.length > 0 && !selectedPen) {
      setSelectedPen(availablePens[0].value);
    }
  }, [availablePens]);

  useEffect(() => {
    if (selectedPen) {
      fetchData();
    }
  }, [selectedDate, selectedPen]);

  const fetchData = async () => {
    setLoading(true);
    setLoadingMortality(true);
    try {
      // Fetch entries for selected date, summary, latest census, and pen mortality
      const [entriesRes, summaryRes, latestRes, mortalityRes] = await Promise.all([
        axios.get(`${API_URL}/api/chicken-census?date=${selectedDate}&pen_number=${selectedPen}`, getAuthHeader()),
        axios.get(`${API_URL}/api/chicken-census/summary?date=${selectedDate}`, getAuthHeader()),
        axios.get(`${API_URL}/api/chicken-census/latest/${selectedPen}?date=${selectedDate}`, getAuthHeader()),
        axios.get(`${API_URL}/api/pen-mortality/daily-summary?date=${selectedDate}`, getAuthHeader())
      ]);
      
      setEntries(entriesRes.data);
      setSummary(summaryRes.data);
      setLatestCensus(latestRes.data);
      
      // Store pen mortality data from Pen Floor Daily Egg Production
      const penMortality = mortalityRes.data?.pen_mortality || {};
      setPenMortalityData(penMortality);
      
      // Get mortality for selected pen from Pen Floor Production
      const mortalityFromProduction = penMortality[selectedPen]?.count || 0;
      const mortalityCauseFromProduction = penMortality[selectedPen]?.cause || '';
      
      // Pre-fill starting census from latest entry
      const startingCensus = latestRes.data?.starting_census_for_next || 0;
      const existingEntry = entriesRes.data.find(e => e.pen_number === selectedPen);
      
      if (existingEntry) {
        // Use existing entry data but update mortality from Pen Floor Production
        setFormData({
          starting_census: existingEntry.starting_census.toString(),
          mortality: mortalityFromProduction.toString(),
          cause_of_death: mortalityCauseFromProduction || existingEntry.cause_of_death || '',
          ending_census: (existingEntry.starting_census - mortalityFromProduction).toString(),
          notes: existingEntry.notes || ''
        });
      } else {
        // New entry - use mortality from Pen Floor Production
        setFormData({
          starting_census: startingCensus.toString(),
          mortality: mortalityFromProduction.toString(),
          cause_of_death: mortalityCauseFromProduction,
          ending_census: (startingCensus - mortalityFromProduction).toString(),
          notes: ''
        });
      }
    } catch (error) {
      console.error('Error fetching census data:', error);
      toast.error('Failed to load census data');
    } finally {
      setLoading(false);
      setLoadingMortality(false);
    }
  };

  const fetchHistoryData = async () => {
    setLoadingHistory(true);
    try {
      const response = await axios.get(`${API_URL}/api/chicken-census/history?days=${historyDays}`, getAuthHeader());
      setHistoryData(response.data);
    } catch (error) {
      console.error('Error fetching history data:', error);
      toast.error('Failed to load historical data');
    } finally {
      setLoadingHistory(false);
    }
  };

  useEffect(() => {
    if (showCharts) {
      fetchHistoryData();
    }
  }, [showCharts, historyDays]);

  const handleMortalityChange = (value) => {
    const mortality = parseInt(value) || 0;
    const starting = parseInt(formData.starting_census) || 0;
    setFormData({
      ...formData,
      mortality: value,
      ending_census: (starting - mortality).toString()
    });
  };

  const handleStartingCensusChange = (value) => {
    const starting = parseInt(value) || 0;
    const mortality = parseInt(formData.mortality) || 0;
    setFormData({
      ...formData,
      starting_census: value,
      ending_census: (starting - mortality).toString()
    });
  };

  const handleSaveCensus = async () => {
    if (!formData.starting_census) {
      toast.error('Please enter starting census');
      return;
    }

    setSaving(true);
    try {
      const existingEntry = entries.find(e => e.pen_number === selectedPen);
      
      const data = {
        date: selectedDate,
        pen_number: selectedPen,
        starting_census: parseInt(formData.starting_census),
        mortality: parseInt(formData.mortality) || 0,
        cause_of_death: formData.cause_of_death || null,
        ending_census: parseInt(formData.ending_census),
        notes: formData.notes || null
      };

      if (existingEntry) {
        // Update existing entry
        const response = await axios.put(`${API_URL}/api/chicken-census/${existingEntry.id}`, data, getAuthHeader());
        if (response.data.batch_synced) {
          toast.success(`${getPenLabel(selectedPen)} census updated. Batch "${response.data.batch_sync_details.batch_name}" updated.`);
        } else {
          toast.success(`${getPenLabel(selectedPen)} census updated`);
        }
      } else {
        // Create new entry
        const response = await axios.post(`${API_URL}/api/chicken-census`, data, getAuthHeader());
        if (response.data.batch_synced) {
          toast.success(`${getPenLabel(selectedPen)} census recorded. Batch "${response.data.batch_sync_details.batch_name}" mortality synced.`);
        } else {
          toast.success(`${getPenLabel(selectedPen)} census recorded`);
        }
      }
      
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save census');
    } finally {
      setSaving(false);
    }
  };

  const handleRecordPhysicalCount = async () => {
    if (!physicalCountForm.physical_count) {
      toast.error('Please enter the physical count');
      return;
    }

    try {
      const response = await axios.post(`${API_URL}/api/chicken-census/physical-count`, {
        date: selectedDate,
        pen_number: selectedPen,
        physical_count: parseInt(physicalCountForm.physical_count),
        notes: physicalCountForm.notes || null
      }, getAuthHeader());

      toast.success(`Physical count recorded. Variance: ${response.data.variance}`);
      setPhysicalCountOpen(false);
      setPhysicalCountForm({ physical_count: '', notes: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record physical count');
    }
  };

  const handleDeleteEntry = async (entryId) => {
    if (!window.confirm('Delete this census entry?')) return;
    
    try {
      const response = await axios.delete(`${API_URL}/api/chicken-census/${entryId}`, getAuthHeader());
      if (response.data.batch_synced) {
        toast.success(`Census entry deleted. Batch "${response.data.batch_sync_details.batch_name}" mortality adjusted.`);
      } else {
        toast.success('Census entry deleted');
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete entry');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric'
    });
  };

  return (
    <div className="space-y-6" data-testid="chicken-census-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Chicken Census</h1>
          <p className="text-slate-600">Daily census tracking for each pen</p>
        </div>
        {(isAdmin || isManager) && (
          <Button 
            variant="outline" 
            onClick={() => setPhysicalCountOpen(true)}
            className="border-amber-500 text-amber-600 hover:bg-amber-50"
            data-testid="physical-count-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Record Physical Count
          </Button>
        )}
      </div>

      {/* Date and Pen Selection */}
      <div className="card">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Select Date</Label>
            <Input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="mt-1"
              data-testid="date-selector"
            />
          </div>
          <div>
            <Label>Select Pen</Label>
            <Select value={selectedPen ? selectedPen.toString() : ""} onValueChange={(v) => setSelectedPen(parseInt(v))}>
              <SelectTrigger className="mt-1" data-testid="pen-selector">
                <SelectValue placeholder="Select pen..." />
              </SelectTrigger>
              <SelectContent>
                {availablePens.map(pen => (
                  <SelectItem key={pen.value} value={pen.value.toString()}>
                    {pen.label}
                    {summary?.pen_census?.[pen.value] && (
                      <span className="ml-2 text-green-500">✓</span>
                    )}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Conspicuous TOTAL CENSUS across ALL pens for the selected date */}
      {summary && (
        <div
          className="rounded-2xl p-6 bg-gradient-to-r from-emerald-600 to-teal-600 text-white shadow-lg flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
          data-testid="total-census-all-pens-banner"
        >
          <div className="flex items-center gap-3">
            <div className="bg-white/15 rounded-xl p-3">
              <Bird className="w-7 h-7" />
            </div>
            <div>
              <p className="text-sm text-white/80 uppercase tracking-wide">Total Census — All Pens</p>
              <p className="text-xs text-white/70">As of {formatDate(selectedDate)} · counts physical updates &amp; mortalities daily</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-4xl font-extrabold leading-none" data-testid="total-census-all-pens-value">
              {(summary.total_all_pens_ending ?? 0).toLocaleString()}
            </p>
            <p className="text-xs text-white/80 mt-1">{summary.pens_counted ?? 0} pen(s) counted</p>
          </div>
        </div>
      )}

      {/* Small 30-day total census trend chart */}
      {summary && Array.isArray(summary.total_trend) && summary.total_trend.some(d => d.total > 0) && (
        <div className="card" data-testid="census-trend-chart">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <TrendingDown className="w-4 h-4 text-emerald-600" />
              Total Census Trend — last 30 days
            </h3>
            <span className="text-xs text-slate-400">ending {formatDate(selectedDate)}</span>
          </div>
          <ResponsiveContainer width="100%" height={150}>
            <ComposedChart data={summary.total_trend} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f6" vertical={false} />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 10, fill: '#94a3b8' }}
                tickFormatter={(d) => { const p = d.split('-'); return `${p[2]}/${p[1]}`; }}
                interval={4}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                yAxisId="total"
                tick={{ fontSize: 10, fill: '#94a3b8' }}
                domain={['dataMin - 20', 'dataMax + 20']}
                axisLine={false}
                tickLine={false}
                width={48}
                tickFormatter={(v) => v.toLocaleString()}
              />
              <YAxis
                yAxisId="mortality"
                orientation="right"
                tick={{ fontSize: 10, fill: '#f87171' }}
                domain={[0, (dataMax) => Math.max(10, Math.ceil(dataMax * 4))]}
                axisLine={false}
                tickLine={false}
                width={28}
                allowDecimals={false}
              />
              <Tooltip
                formatter={(v, name) => [v.toLocaleString(), name === 'mortality' ? 'Mortality' : 'Total Census']}
                labelFormatter={(d) => formatDate(d)}
                contentStyle={{ fontSize: 12, borderRadius: 8 }}
              />
              <Bar yAxisId="mortality" dataKey="mortality" fill="#fecaca" radius={[2, 2, 0, 0]} barSize={10} name="mortality" />
              <Line yAxisId="total" type="monotone" dataKey="total" stroke="#059669" strokeWidth={2} dot={false} activeDot={{ r: 4 }} name="total" />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Daily Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="card bg-blue-50 border-blue-200">
            <div className="text-sm text-blue-600">Total Starting</div>
            <div className="text-2xl font-bold text-blue-700">{summary.totals.starting.toLocaleString()}</div>
          </div>
          <div className="card bg-red-50 border-red-200">
            <div className="text-sm text-red-600 flex items-center gap-1">
              Total Mortality
              {penMortalityData && Object.keys(penMortalityData).length > 0 && (
                <span className="text-xs bg-amber-100 text-amber-600 px-1 rounded">Pen Floor</span>
              )}
            </div>
            <div className="text-2xl font-bold text-red-700">
              {penMortalityData && Object.keys(penMortalityData).length > 0
                ? Object.values(penMortalityData).reduce((sum, m) => sum + (m?.count || 0), 0)
                : summary.totals.mortality}
            </div>
          </div>
          <div className="card bg-green-50 border-green-200">
            <div className="text-sm text-green-600">Total Ending</div>
            <div className="text-2xl font-bold text-green-700">{summary.totals.ending.toLocaleString()}</div>
          </div>
          <div className="card bg-purple-50 border-purple-200">
            <div className="text-sm text-purple-600">Pens Recorded</div>
            <div className="text-2xl font-bold text-purple-700">{Object.keys(summary.pen_census).length} / 8</div>
          </div>
        </div>
      )}

      {/* Census Entry Form */}
      {selectedPen && (
        <div className="card border-2 border-emerald-200">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-slate-800 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-emerald-600" />
              {getPenLabel(selectedPen)} - {formatDate(selectedDate)}
            </h2>
            {entries.find(e => e.pen_number === selectedPen) && (
              <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-sm flex items-center gap-1">
                <CheckCircle className="w-4 h-4" />
                Recorded
              </span>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <Label className="text-slate-700">Starting Census *</Label>
              <Input
                type="number"
                min="0"
                value={formData.starting_census}
                onChange={(e) => handleStartingCensusChange(e.target.value)}
                className="mt-1"
                data-testid="starting-census-input"
              />
              <p className="text-xs text-slate-500 mt-1">
                Suggested: {latestCensus?.starting_census_for_next || 0}
              </p>
            </div>
            <div>
              <Label className="text-red-700 flex items-center gap-2">
                Mortality
                {penMortalityData && penMortalityData[selectedPen] && (
                  <span className="text-xs font-normal text-amber-600 bg-amber-50 px-2 py-0.5 rounded">
                    From Pen Floor Production
                  </span>
                )}
              </Label>
              <Input
                type="number"
                min="0"
                value={formData.mortality}
                readOnly={!!(penMortalityData && penMortalityData[selectedPen])}
                onChange={(e) => !penMortalityData?.[selectedPen] && handleMortalityChange(e.target.value)}
                className={`mt-1 border-red-200 ${penMortalityData?.[selectedPen] ? 'bg-red-50 cursor-not-allowed' : 'focus:border-red-400'}`}
                data-testid="mortality-input"
              />
              {penMortalityData && penMortalityData[selectedPen] ? (
                <p className="text-xs text-amber-600 mt-1">
                  Pulled from "Pen Floor Daily Egg Production & Mortalities"
                </p>
              ) : (
                <p className="text-xs text-slate-500 mt-1">
                  No mortality recorded in Pen Floor Production for this date
                </p>
              )}
            </div>
            <div>
              <Label className="text-slate-700">Cause of Death</Label>
              {penMortalityData && penMortalityData[selectedPen] ? (
                <>
                  <Input
                    value={formData.cause_of_death || 'Not specified'}
                    readOnly
                    className="mt-1 bg-slate-50 cursor-not-allowed"
                    data-testid="cause-display"
                  />
                  <p className="text-xs text-amber-600 mt-1">From Pen Floor Production</p>
                </>
              ) : (
                <Select value={formData.cause_of_death || "none"} onValueChange={(v) => setFormData({...formData, cause_of_death: v === "none" ? "" : v})}>
                  <SelectTrigger className="mt-1" data-testid="cause-select">
                    <SelectValue placeholder="Select cause..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {MORTALITY_CAUSES.map(cause => (
                      <SelectItem key={cause} value={cause}>{cause}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            <div>
              <Label className="text-emerald-700">Ending Census</Label>
              <Input
                type="number"
                value={formData.ending_census}
                onChange={(e) => setFormData({...formData, ending_census: e.target.value})}
                className="mt-1 border-emerald-200 bg-emerald-50"
                data-testid="ending-census-input"
              />
              <p className="text-xs text-slate-500 mt-1">Auto-calculated</p>
            </div>
            <div className="md:col-span-2">
              <Label className="text-slate-700">Notes (Optional)</Label>
              <Input
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                placeholder="Any additional notes..."
                className="mt-1"
                data-testid="notes-input"
              />
            </div>
          </div>

          <div className="flex items-center gap-2 mt-4">
            <Button
              onClick={handleSaveCensus}
              disabled={saving || !formData.starting_census}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="save-census-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : entries.find(e => e.pen_number === selectedPen) ? 'Update Census' : 'Save Census'}
            </Button>
            {isAdmin && entries.find(e => e.pen_number === selectedPen) && (
              <Button
                variant="outline"
                onClick={() => handleDeleteEntry(entries.find(e => e.pen_number === selectedPen).id)}
                className="text-red-600 hover:bg-red-50"
                data-testid="delete-census-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </Button>
            )}
          </div>
        </div>
      )}

      {/* All Pens Overview for Today */}
      <div className="card">
        <h3 className="text-lg font-semibold text-slate-800 mb-4">All Pens Status - {formatDate(selectedDate)}</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {PENS.map(pen => {
            const penData = summary?.pen_census?.[pen.value];
            const penMortality = penMortalityData?.[pen.value];
            const isAccessible = isAdmin || isManager || assignedPens.includes(pen.value) || !assignedPens.length;
            
            return (
              <div
                key={pen.value}
                className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                  penData 
                    ? 'bg-emerald-50 border-emerald-300' 
                    : 'bg-slate-50 border-slate-200'
                } ${selectedPen === pen.value ? 'ring-2 ring-emerald-500' : ''} ${
                  !isAccessible ? 'opacity-50' : ''
                }`}
                onClick={() => isAccessible && setSelectedPen(pen.value)}
              >
                <div className="font-semibold text-slate-800 flex items-center justify-between">
                  <span>{pen.label}</span>
                  {penData && <CheckCircle className="w-4 h-4 text-emerald-600" />}
                </div>
                {penData ? (
                  <div className="text-sm mt-1 space-y-0.5">
                    <div className="flex justify-between">
                      <span className="text-slate-500">Start:</span>
                      <span className="font-medium">{penData.starting_census}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-red-500">Mortality:</span>
                      <span className="font-medium text-red-600">{penMortality?.count || penData.mortality || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-emerald-500">End:</span>
                      <span className="font-medium text-emerald-600">{penData.ending_census}</span>
                    </div>
                  </div>
                ) : penMortality ? (
                  <div className="text-sm mt-1 space-y-0.5">
                    <div className="flex justify-between">
                      <span className="text-red-500">Mortality:</span>
                      <span className="font-medium text-red-600">{penMortality.count}</span>
                    </div>
                    <div className="text-xs text-amber-600 mt-1">From Pen Floor</div>
                  </div>
                ) : (
                  <div className="text-sm text-slate-400 mt-1">No entry</div>
                )}
                {!isAccessible && (
                  <div className="text-xs text-slate-400 mt-1">Not assigned</div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Historical Charts Section */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-blue-600" />
            Mortality Trends & Analytics
          </h3>
          <div className="flex items-center gap-3">
            <Select value={historyDays.toString()} onValueChange={(v) => setHistoryDays(parseInt(v))}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="14">Last 14 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="60">Last 60 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant={showCharts ? "default" : "outline"}
              onClick={() => setShowCharts(!showCharts)}
              className={showCharts ? "bg-blue-600 hover:bg-blue-700" : ""}
            >
              {showCharts ? 'Hide Charts' : 'Show Charts'}
            </Button>
          </div>
        </div>

        {showCharts && (
          <div className="space-y-6">
            {loadingHistory ? (
              <div className="text-center py-8 text-slate-500">Loading historical data...</div>
            ) : historyData ? (
              <>
                {/* Summary Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <div className="text-sm text-red-600">Total Mortality</div>
                    <div className="text-2xl font-bold text-red-700">{historyData.summary?.total_mortality || 0}</div>
                    <div className="text-xs text-red-500">in {historyDays} days</div>
                  </div>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <div className="text-sm text-blue-600">Census Entries</div>
                    <div className="text-2xl font-bold text-blue-700">{historyData.summary?.total_entries || 0}</div>
                    <div className="text-xs text-blue-500">recorded</div>
                  </div>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <div className="text-sm text-amber-600">Avg Daily Mortality</div>
                    <div className="text-2xl font-bold text-amber-700">{historyData.summary?.avg_daily_mortality || 0}</div>
                    <div className="text-xs text-amber-500">birds/day</div>
                  </div>
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                    <div className="text-sm text-emerald-600">Days with Data</div>
                    <div className="text-2xl font-bold text-emerald-700">{historyData.daily_trend?.length || 0}</div>
                    <div className="text-xs text-emerald-500">out of {historyDays}</div>
                  </div>
                </div>

                {/* Daily Mortality Trend Line Chart */}
                {historyData.daily_trend?.length > 0 && (
                  <div className="bg-white border rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                      <TrendingDown className="w-4 h-4 text-red-500" />
                      Daily Mortality Trend
                    </h4>
                    <ResponsiveContainer width="100%" height={250}>
                      <LineChart data={historyData.daily_trend}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis 
                          dataKey="date" 
                          tick={{ fontSize: 11 }}
                          tickFormatter={(val) => new Date(val).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                        />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip 
                          labelFormatter={(val) => new Date(val).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                          contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }}
                        />
                        <Legend />
                        <Line type="monotone" dataKey="mortality" stroke="#ef4444" strokeWidth={2} name="Mortality" dot={{ r: 4 }} />
                        <Line type="monotone" dataKey="pens_recorded" stroke="#3b82f6" strokeWidth={2} name="Pens Recorded" dot={{ r: 3 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {/* Mortality by Pen Bar Chart */}
                {historyData.pen_mortality_ranking?.length > 0 && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-white border rounded-lg p-4">
                      <h4 className="text-sm font-semibold text-slate-700 mb-3">Mortality by Pen</h4>
                      <ResponsiveContainer width="100%" height={220}>
                        <BarChart data={historyData.pen_mortality_ranking} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                          <XAxis type="number" tick={{ fontSize: 11 }} />
                          <YAxis 
                            type="category" 
                            dataKey="pen_name" 
                            tick={{ fontSize: 11 }} 
                            width={80}
                          />
                          <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }} />
                          <Bar dataKey="total_mortality" name="Mortality" radius={[0, 4, 4, 0]}>
                            {historyData.pen_mortality_ranking.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>

                    {/* Mortality by Cause Pie Chart */}
                    {Object.keys(historyData.cause_breakdown || {}).length > 0 && (
                      <div className="bg-white border rounded-lg p-4">
                        <h4 className="text-sm font-semibold text-slate-700 mb-3">Mortality by Cause</h4>
                        <ResponsiveContainer width="100%" height={220}>
                          <PieChart>
                            <Pie
                              data={Object.entries(historyData.cause_breakdown).map(([name, value]) => ({ name, value }))}
                              cx="50%"
                              cy="50%"
                              innerRadius={50}
                              outerRadius={80}
                              paddingAngle={2}
                              dataKey="value"
                              label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                              labelLine={{ strokeWidth: 1 }}
                            >
                              {Object.entries(historyData.cause_breakdown).map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }} />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                    )}
                  </div>
                )}

                {historyData.daily_trend?.length === 0 && (
                  <div className="text-center py-8 text-slate-500">
                    <BarChart3 className="w-12 h-12 mx-auto mb-2 opacity-30" />
                    <p>No census data available for the selected period.</p>
                    <p className="text-sm">Start recording daily census entries to see trends.</p>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-8 text-slate-500">Click "Show Charts" to load historical data</div>
            )}
          </div>
        )}
      </div>

      {/* Physical Count Dialog */}
      <Dialog open={physicalCountOpen} onOpenChange={setPhysicalCountOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="w-5 h-5 text-amber-500" />
              Record Physical Count - {getPenLabel(selectedPen)}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm">
              <p className="text-amber-700">
                Use this when you perform a physical count to reconcile the census.
                The physical count will become the starting census for the next day.
              </p>
            </div>
            <div>
              <Label>Date of Count</Label>
              <Input type="date" value={selectedDate} disabled className="mt-1 bg-slate-100" />
            </div>
            <div>
              <Label>Physical Count (Actual birds counted) *</Label>
              <Input
                type="number"
                min="0"
                value={physicalCountForm.physical_count}
                onChange={(e) => setPhysicalCountForm({...physicalCountForm, physical_count: e.target.value})}
                placeholder="Enter count..."
                className="mt-1"
                data-testid="physical-count-input"
              />
            </div>
            {latestCensus?.latest_entry && (
              <div className="p-2 bg-slate-50 rounded text-sm">
                <span className="text-slate-500">Previous ending census:</span>
                <span className="ml-2 font-medium">{latestCensus.latest_entry.ending_census}</span>
                {physicalCountForm.physical_count && (
                  <span className={`ml-2 font-medium ${
                    parseInt(physicalCountForm.physical_count) !== latestCensus.latest_entry.ending_census
                      ? 'text-amber-600' : 'text-green-600'
                  }`}>
                    (Variance: {parseInt(physicalCountForm.physical_count) - latestCensus.latest_entry.ending_census})
                  </span>
                )}
              </div>
            )}
            <div>
              <Label>Notes (Optional)</Label>
              <Textarea
                value={physicalCountForm.notes}
                onChange={(e) => setPhysicalCountForm({...physicalCountForm, notes: e.target.value})}
                placeholder="Any notes about the count..."
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPhysicalCountOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleRecordPhysicalCount}
              className="bg-amber-500 hover:bg-amber-600"
              data-testid="save-physical-count-btn"
            >
              Record Count
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ChickenCensus;
